import { useEffect, useState } from "react";

import apiClient from "../../../api/client";
import { isCanceledRequest } from "../../../helpers/isCanceledRequest";

export const SUGGEST_URL = "/members/api-v2/search/suggest";

const IDLE = { status: "idle", data: null };

/**
 * GET /api-v2/search/suggest for the header search (GROWTH-7577). Asks again whenever the text, scope or
 * city changes, and cancels the request the change made stale so an old answer never lands over a newer one.
 * The previous answer stays while the next loads.
 *
 * With no text, a Browse chip (`browse`) asks for the newest of one scope (GROWTH-7646). That answer is never
 * kept across a change, so one scope's rows do not sit under another's chip while it loads.
 *
 * @param {string} query the trimmed text; empty asks nothing unless `browse`
 * @param {string} scope all | profile | organization | event | job | community
 * @param {?string} city the city to search near, or null for anywhere
 * @param {boolean} [browse] ask for the scope's newest when the text is empty (scope is one type, not "all")
 * @returns {{status: "idle"|"loading"|"ready"|"error", data: ?{counts: Object, groups: Object[]}}}
 */
export const useSuggest = (query, scope, city, browse = false) => {
  const [state, setState] = useState(IDLE);

  useEffect(() => {
    const browsing = query === "" && browse && scope !== "all";
    if (query === "" && !browsing) {
      setState(IDLE);
      return undefined;
    }

    const controller = new AbortController();
    setState(previous => (browsing ? { status: "loading", data: null } : { ...previous, status: "loading" }));
    apiClient
      .get(SUGGEST_URL, { params: { q: query || undefined, scope, location: city || undefined }, signal: controller.signal })
      .then(response => {
        if (!controller.signal.aborted) setState({ status: "ready", data: response.data.data });
      })
      .catch(error => {
        if (!isCanceledRequest(error) && !controller.signal.aborted) setState({ status: "error", data: null });
      });

    return () => controller.abort();
  }, [query, scope, city, browse]);

  return state;
};
